/**
 * Helper to check and filter sidebar navigation items for HOD users.
 */

import { TOP_DASHBOARD_NAV_NAME } from './topDashboardAccess';

// Array of roles that trigger this simplified HOD/Supervisor sidebar.
// You can add more roles to this array in the future.
export const HOD_ROLES = ["HOD", "TEST_HOD"];

/**
 * Check if the user has any of the HOD roles
 */
export const hasHodRole = (user) => {
  if (!user) return false;
  
  // Check primary role
  const primaryRoleCode = typeof user.primaryRole === 'string'
    ? user.primaryRole
    : user.primaryRole?.roleCode;
  if (HOD_ROLES.includes(primaryRoleCode) || HOD_ROLES.includes(user.role)) {
    return true;
  }
  
  // Check roles array
  if (user.roles && Array.isArray(user.roles)) {
    return user.roles.some(role => {
      if (typeof role === 'string') return HOD_ROLES.includes(role);
      if (typeof role === 'object' && role) {
        return HOD_ROLES.includes(role.roleCode) || HOD_ROLES.includes(role.code) || HOD_ROLES.includes(role.name);
      }
      return false;
    });
  }
  
  return false;
};

/**
 * Filter and flatten the sidebar groups for HOD users, adding headers for Employee Self-Service and Approve
 * @param {Array} originalGroups
 * @param {{ canViewTopDashboard?: boolean }} [options]
 */
export const getHodEmployeeNavigation = (originalGroups, options = {}) => {
  const { canViewTopDashboard = false } = options;

  return originalGroups.map(group => {
    const newItems = [];
    
    group.items.forEach(item => {
      // Menu items to hide
      const namesToHide = [
        "Time & Attendance",
        "Leave Management",
        "Performance Management",
        "Self Dashboard",
        "Admin Dashboard",
        "Employee Management",
        "Setup",
        "Salary Payments",
        "Roster Management",
        "Shift Setup",
        "Roster Planner",
        "Roster Template",
        "Roster Verification",
        "Daily Verification",
        "Bulk Generation",
        "Category Management",
        "Salary Component Configuration",
        "OT Approval Config",
        "Dashboard",
        "My Dashboard"
      ];
      
      if (namesToHide.includes(item.name)) {
        // Optional override: show top Overview Dashboard when permission is granted
        if (item.name === TOP_DASHBOARD_NAV_NAME && canViewTopDashboard) {
          newItems.push(item);
        }
        return; // skip this item
      }
      
      // If it is "Employee Self-Service", add header label and flatten subItems
      if (item.name === "Employee Self-Service" && item.subItems) {
        newItems.push({
          name: "Employee Self-Service",
          isHeader: true
        });
        
        item.subItems.forEach(subItem => {
          newItems.push({
            ...subItem,
            isSubmenu: false
          });
        });
        return;
      }
      
      // If it is "Approve", add header label and flatten subItems
      if (item.name === "Approve" && item.subItems) {
        newItems.push({
          name: "Approve",
          isHeader: true
        });
        
        item.subItems.forEach(subItem => {
          newItems.push({
            ...subItem,
            isSubmenu: false
          });
        });
        return;
      }
      
      newItems.push(item);
    });
    
    return {
      ...group,
      items: newItems
    };
  }).filter(group => {
    // Group names to hide entirely
    const groupsToHide = ["Time & Attendance", "Leave Management", "Performance Management", "Payroll Management"];
    if (groupsToHide.includes(group.name)) {
      return false;
    }
    return group.items.length > 0;
  });
};
